import React, { useCallback, useEffect, useRef, useState } from 'react';
import { ArrowLeft, Loader, MessageCircle, Package, ShieldCheck, Smartphone } from 'lucide-react';
import { createOrGetFinalClientDeliveryConversation } from '../shared/communication/api';
import ConversationThread from '../shared/communication/ConversationThread';

const TERMINAL_STATUSES = new Set(['entregado', 'rechazado', 'cancelado']);

export default function FinalClientChat({ delivery, onBack }) {
  const [conversation, setConversation] = useState(null);
  const [socketTicket, setSocketTicket] = useState(null);
  const [canSend, setCanSend] = useState(!TERMINAL_STATUSES.has((delivery?.status || '').toLowerCase()));
  const [reason, setReason] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const lastTicketRefreshRef = useRef(0);

  const loadConversation = useCallback(async () => {
    if (!delivery?.id) return;
    setLoading(true);
    setError(null);
    try {
      const data = await createOrGetFinalClientDeliveryConversation(delivery.id);
      setConversation(data.conversation || null);
      setSocketTicket(data.socket_ticket || null);
      lastTicketRefreshRef.current = Date.now();
      setCanSend(Boolean(data.can_send));
      setReason(data.reason || null);
    } catch (err) {
      console.error('Final client chat error:', err);
      setError('No pudimos cargar el chat de esta entrega. Intenta nuevamente.');
    } finally {
      setLoading(false);
    }
  }, [delivery?.id]);

  useEffect(() => {
    loadConversation();
  }, [loadConversation]);

  useEffect(() => {
    const refreshTicketIfNeeded = () => {
      if (Date.now() - lastTicketRefreshRef.current >= 45 * 60 * 1000) loadConversation();
    };
    const timer = window.setInterval(refreshTicketIfNeeded, 45 * 60 * 1000);
    window.addEventListener('focus', refreshTicketIfNeeded);
    return () => {
      window.clearInterval(timer);
      window.removeEventListener('focus', refreshTicketIfNeeded);
    };
  }, [loadConversation]);

  return (
    <div className="flex h-screen w-screen flex-col overflow-hidden bg-slate-100">
      <div className="flex-shrink-0 bg-gradient-to-r from-brand-primary to-pink-600 shadow-lg">
        <div className="mx-auto max-w-4xl px-4 py-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-3">
            <button
              onClick={onBack}
              className="rounded-lg p-2 transition-colors duration-200 hover:bg-white/10 active:scale-95"
            >
              <ArrowLeft className="h-6 w-6 text-white" />
            </button>
            <div className="flex items-center gap-3">
              <div className="rounded-lg bg-white p-2 shadow-md">
                <MessageCircle className="h-6 w-6 text-brand-primary" />
              </div>
              <div>
                <h1 className="text-xl font-extrabold text-white font-montserrat sm:text-2xl">
                  Chat de entrega
                </h1>
                <p className="text-xs text-white/90 font-montserrat">
                  Entrega #{delivery?.id} · {delivery?.recipient_name || 'Cliente'}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto p-4">
        <div className="mx-auto flex h-full max-w-4xl flex-col gap-3">
          <div className="grid gap-3 md:grid-cols-3">
            <div className="rounded-xl border border-slate-200 bg-white p-3 text-xs text-slate-600 shadow-sm">
              <div className="mb-1 flex items-center gap-2 font-bold text-slate-900">
                <Package className="h-4 w-4 text-[#B1336E]" />
                Hilo de entrega
              </div>
              Tu mensaje queda conectado a esta entrega para Fast Max, tienda y motorizado.
            </div>
            <div className="rounded-xl border border-slate-200 bg-white p-3 text-xs text-slate-600 shadow-sm">
              <div className="mb-1 flex items-center gap-2 font-bold text-slate-900">
                <Smartphone className="h-4 w-4 text-emerald-600" />
                WhatsApp externo
              </div>
              WhatsApp puede aparecer como historial, pero este chat usa Fast Max internamente.
            </div>
            <div className="rounded-xl border border-slate-200 bg-white p-3 text-xs text-slate-600 shadow-sm">
              <div className="mb-1 flex items-center gap-2 font-bold text-slate-900">
                <ShieldCheck className="h-4 w-4 text-sky-600" />
                Hasta finalizar
              </div>
              Cuando la entrega termine, el chat quedará como historial operativo.
            </div>
          </div>

          {reason && (
            <div className="rounded-xl border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
              {reason}
            </div>
          )}

          {error && (
            <div className="rounded-xl border border-red-200 bg-red-50 p-3 text-sm text-red-700">
              {error}
            </div>
          )}

          {loading && !conversation && (
            <div className="flex flex-1 items-center justify-center rounded-xl border border-slate-200 bg-white text-slate-500">
              <Loader className="mr-2 h-5 w-5 animate-spin" />
              Cargando chat...
            </div>
          )}

          {conversation && (
            <ConversationThread
              conversationId={conversation.id}
              socketTicket={socketTicket}
              currentUserType="final_customer"
              className="min-h-0 flex-1"
              allowNotes={false}
              showInput={canSend}
              inputPlaceholder="Escribe tu mensaje sobre esta entrega..."
              inputHelperText="Mensaje enviado desde Fast Max App. No se reenvia automaticamente a WhatsApp."
            />
          )}
        </div>
      </div>
    </div>
  );
}
